'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { AlertCircle, CreditCard, CheckCircle2, Building2 } from 'lucide-react';
import { 
  DonationSettings, 
  DynamicField 
} from '@/types/firebase';
import { PaymentModal } from '@/components/PaymentModal';
import { ACHPaymentModal } from '@/components/ACHPaymentModal';
import { v4 as uuidv4 } from 'uuid';

// Import ShadCN components
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";

interface DonationFormProps {
  companyId: string;
  companyName: string;
  donationSettings: DonationSettings;
}

// Country list with Canada and United States at the top
const COUNTRIES = [
  { value: "Canada", label: "Canada" },
  { value: "United States", label: "United States" },
  { value: "Afghanistan", label: "Afghanistan" },
  { value: "Albania", label: "Albania" },
  { value: "Algeria", label: "Algeria" },
  { value: "Andorra", label: "Andorra" },
  { value: "Angola", label: "Angola" },
  { value: "Antigua and Barbuda", label: "Antigua and Barbuda" },
  { value: "Argentina", label: "Argentina" },
  { value: "Armenia", label: "Armenia" },
  { value: "Australia", label: "Australia" },
  { value: "Austria", label: "Austria" },
  { value: "Azerbaijan", label: "Azerbaijan" },
  { value: "Bahamas", label: "Bahamas" },
  { value: "Bahrain", label: "Bahrain" },
  { value: "Bangladesh", label: "Bangladesh" },
  { value: "Barbados", label: "Barbados" },
  { value: "Belarus", label: "Belarus" },
  { value: "Belgium", label: "Belgium" },
  { value: "Belize", label: "Belize" },
  { value: "Benin", label: "Benin" },
  { value: "Bhutan", label: "Bhutan" },
  { value: "Bolivia", label: "Bolivia" },
  { value: "Bosnia and Herzegovina", label: "Bosnia and Herzegovina" },
  { value: "Botswana", label: "Botswana" },
  { value: "Brazil", label: "Brazil" },
  { value: "Brunei", label: "Brunei" },
  { value: "Bulgaria", label: "Bulgaria" },
  { value: "Burkina Faso", label: "Burkina Faso" },
  { value: "Burundi", label: "Burundi" },
  { value: "Cabo Verde", label: "Cabo Verde" },
  { value: "Cambodia", label: "Cambodia" },
  { value: "Cameroon", label: "Cameroon" },
  { value: "Central African Republic", label: "Central African Republic" },
  { value: "Chad", label: "Chad" },
  { value: "Chile", label: "Chile" },
  { value: "China", label: "China" },
  { value: "Colombia", label: "Colombia" },
  { value: "Comoros", label: "Comoros" },
  { value: "Congo", label: "Congo" },
  { value: "Costa Rica", label: "Costa Rica" },
  { value: "Croatia", label: "Croatia" },
  { value: "Cuba", label: "Cuba" },
  { value: "Cyprus", label: "Cyprus" },
  { value: "Czech Republic", label: "Czech Republic" },
  { value: "Denmark", label: "Denmark" },
  { value: "Djibouti", label: "Djibouti" },
  { value: "Dominica", label: "Dominica" },
  { value: "Dominican Republic", label: "Dominican Republic" },
  { value: "Ecuador", label: "Ecuador" },
  { value: "Egypt", label: "Egypt" },
  { value: "El Salvador", label: "El Salvador" },
  { value: "Equatorial Guinea", label: "Equatorial Guinea" },
  { value: "Eritrea", label: "Eritrea" },
  { value: "Estonia", label: "Estonia" },
  { value: "Eswatini", label: "Eswatini" },
  { value: "Ethiopia", label: "Ethiopia" },
  { value: "Fiji", label: "Fiji" },
  { value: "Finland", label: "Finland" },
  { value: "France", label: "France" },
  { value: "Gabon", label: "Gabon" },
  { value: "Gambia", label: "Gambia" },
  { value: "Georgia", label: "Georgia" },
  { value: "Germany", label: "Germany" },
  { value: "Ghana", label: "Ghana" },
  { value: "Greece", label: "Greece" },
  { value: "Grenada", label: "Grenada" },
  { value: "Guatemala", label: "Guatemala" },
  { value: "Guinea", label: "Guinea" },
  { value: "Guinea-Bissau", label: "Guinea-Bissau" },
  { value: "Guyana", label: "Guyana" },
  { value: "Haiti", label: "Haiti" },
  { value: "Honduras", label: "Honduras" },
  { value: "Hungary", label: "Hungary" },
  { value: "Iceland", label: "Iceland" },
  { value: "India", label: "India" },
  { value: "Indonesia", label: "Indonesia" },
  { value: "Iran", label: "Iran" },
  { value: "Iraq", label: "Iraq" },
  { value: "Ireland", label: "Ireland" },
  { value: "Israel", label: "Israel" },
  { value: "Italy", label: "Italy" },
  { value: "Jamaica", label: "Jamaica" },
  { value: "Japan", label: "Japan" },
  { value: "Jordan", label: "Jordan" },
  { value: "Kazakhstan", label: "Kazakhstan" },
  { value: "Kenya", label: "Kenya" },
  { value: "Kiribati", label: "Kiribati" },
  { value: "Korea, North", label: "Korea, North" },
  { value: "Korea, South", label: "Korea, South" },
  { value: "Kosovo", label: "Kosovo" },
  { value: "Kuwait", label: "Kuwait" },
  { value: "Kyrgyzstan", label: "Kyrgyzstan" },
  { value: "Laos", label: "Laos" },
  { value: "Latvia", label: "Latvia" },
  { value: "Lebanon", label: "Lebanon" },
  { value: "Lesotho", label: "Lesotho" },
  { value: "Liberia", label: "Liberia" },
  { value: "Libya", label: "Libya" },
  { value: "Liechtenstein", label: "Liechtenstein" },
  { value: "Lithuania", label: "Lithuania" },
  { value: "Luxembourg", label: "Luxembourg" },
  { value: "Madagascar", label: "Madagascar" },
  { value: "Malawi", label: "Malawi" },
  { value: "Malaysia", label: "Malaysia" },
  { value: "Maldives", label: "Maldives" },
  { value: "Mali", label: "Mali" },
  { value: "Malta", label: "Malta" },
  { value: "Marshall Islands", label: "Marshall Islands" },
  { value: "Mauritania", label: "Mauritania" },
  { value: "Mauritius", label: "Mauritius" },
  { value: "Mexico", label: "Mexico" },
  { value: "Micronesia", label: "Micronesia" },
  { value: "Moldova", label: "Moldova" },
  { value: "Monaco", label: "Monaco" },
  { value: "Mongolia", label: "Mongolia" },
  { value: "Montenegro", label: "Montenegro" },
  { value: "Morocco", label: "Morocco" },
  { value: "Mozambique", label: "Mozambique" },
  { value: "Myanmar", label: "Myanmar" },
  { value: "Namibia", label: "Namibia" },
  { value: "Nauru", label: "Nauru" },
  { value: "Nepal", label: "Nepal" },
  { value: "Netherlands", label: "Netherlands" },
  { value: "New Zealand", label: "New Zealand" },
  { value: "Nicaragua", label: "Nicaragua" },
  { value: "Niger", label: "Niger" },
  { value: "Nigeria", label: "Nigeria" },
  { value: "North Macedonia", label: "North Macedonia" },
  { value: "Norway", label: "Norway" },
  { value: "Oman", label: "Oman" },
  { value: "Pakistan", label: "Pakistan" },
  { value: "Palau", label: "Palau" },
  { value: "Palestine", label: "Palestine" },
  { value: "Panama", label: "Panama" },
  { value: "Papua New Guinea", label: "Papua New Guinea" },
  { value: "Paraguay", label: "Paraguay" },
  { value: "Peru", label: "Peru" },
  { value: "Philippines", label: "Philippines" },
  { value: "Poland", label: "Poland" },
  { value: "Portugal", label: "Portugal" },
  { value: "Qatar", label: "Qatar" },
  { value: "Romania", label: "Romania" },
  { value: "Russia", label: "Russia" },
  { value: "Rwanda", label: "Rwanda" },
  { value: "Saint Kitts and Nevis", label: "Saint Kitts and Nevis" },
  { value: "Saint Lucia", label: "Saint Lucia" },
  { value: "Saint Vincent and the Grenadines", label: "Saint Vincent and the Grenadines" },
  { value: "Samoa", label: "Samoa" },
  { value: "San Marino", label: "San Marino" },
  { value: "Sao Tome and Principe", label: "Sao Tome and Principe" },
  { value: "Saudi Arabia", label: "Saudi Arabia" },
  { value: "Senegal", label: "Senegal" },
  { value: "Serbia", label: "Serbia" },
  { value: "Seychelles", label: "Seychelles" },
  { value: "Sierra Leone", label: "Sierra Leone" },
  { value: "Singapore", label: "Singapore" },
  { value: "Slovakia", label: "Slovakia" },
  { value: "Slovenia", label: "Slovenia" },
  { value: "Solomon Islands", label: "Solomon Islands" },
  { value: "Somalia", label: "Somalia" },
  { value: "South Africa", label: "South Africa" },
  { value: "South Sudan", label: "South Sudan" },
  { value: "Spain", label: "Spain" },
  { value: "Sri Lanka", label: "Sri Lanka" },
  { value: "Sudan", label: "Sudan" },
  { value: "Suriname", label: "Suriname" },
  { value: "Sweden", label: "Sweden" },
  { value: "Switzerland", label: "Switzerland" },
  { value: "Syria", label: "Syria" },
  { value: "Taiwan", label: "Taiwan" },
  { value: "Tajikistan", label: "Tajikistan" },
  { value: "Tanzania", label: "Tanzania" },
  { value: "Thailand", label: "Thailand" },
  { value: "Timor-Leste", label: "Timor-Leste" },
  { value: "Togo", label: "Togo" },
  { value: "Tonga", label: "Tonga" },
  { value: "Trinidad and Tobago", label: "Trinidad and Tobago" },
  { value: "Tunisia", label: "Tunisia" },
  { value: "Turkey", label: "Turkey" },
  { value: "Turkmenistan", label: "Turkmenistan" },
  { value: "Tuvalu", label: "Tuvalu" },
  { value: "Uganda", label: "Uganda" },
  { value: "Ukraine", label: "Ukraine" },
  { value: "United Arab Emirates", label: "United Arab Emirates" },
  { value: "United Kingdom", label: "United Kingdom" },
  { value: "Uruguay", label: "Uruguay" },
  { value: "Uzbekistan", label: "Uzbekistan" },
  { value: "Vanuatu", label: "Vanuatu" },
  { value: "Vatican City", label: "Vatican City" },
  { value: "Venezuela", label: "Venezuela" },
  { value: "Vietnam", label: "Vietnam" },
  { value: "Yemen", label: "Yemen" },
  { value: "Zambia", label: "Zambia" },
  { value: "Zimbabwe", label: "Zimbabwe" }
];

// Create a dynamic schema based on donation settings
const createDonationSchema = (dynamicFields: DynamicField[], collectAddress: boolean) => {
  const baseSchema = {
    firstName: z.string().min(1, "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    email: z.string().email("Please enter a valid email address").min(1, "Email is required"),
    amount: z.coerce.number().min(0.01, "Amount must be greater than 0"),
    address: collectAddress ? z.string().min(1, "Address is required") : z.string().optional(),
    country: collectAddress ? z.string().min(1, "Country is required") : z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    zip: collectAddress ? z.string().min(1, "ZIP code is required") : z.string().optional(),
  } as Record<string, any>;
  
  // Add dynamic fields to schema (excluding disabled fields)
  dynamicFields
    .filter(field => !field.disabled)
    .forEach(field => {
      if (field.required) {
        baseSchema[field.name] = z.string().min(1, `${field.label} is required`);
      } else {
        baseSchema[field.name] = z.string().optional();
      }
    });
  
  return z.object(baseSchema);
};

export default function DonationForm({ 
  companyId, 
  donationSettings 
}: DonationFormProps) {
  const [_isProcessing, setIsProcessing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customAmount, setCustomAmount] = useState(false);
  const [selectedPresetAmount, setSelectedPresetAmount] = useState<number | null>(null);
  const [urlAmountOverride, setUrlAmountOverride] = useState(false);
  const [platformFeeAmount, setPlatformFeeAmount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [customerCoversFee, setCustomerCoversFee] = useState(false);
  const isAutoFee = donationSettings.platformFeeEnabled && donationSettings.platformFeeMode !== 'customer_choice';
  const isCustomerChoiceFee = donationSettings.platformFeeEnabled && donationSettings.platformFeeMode === 'customer_choice';
  const showFeeToCustomer = isAutoFee || (isCustomerChoiceFee && customerCoversFee);

  // Payment modal states
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isACHPaymentModalOpen, setIsACHPaymentModalOpen] = useState(false);
  const [donationDetails, setDonationDetails] = useState<any>(null);
  
  const amountInputRef = useRef<HTMLInputElement>(null);

  // Create dynamic schema based on donation settings
  const donationSchema = createDonationSchema(
    donationSettings.dynamicFields,
    donationSettings.collectAddress !== false
  );
  type DonationFormData = z.infer<typeof donationSchema>;
  
  // Initialize form with default values
  const form = useForm<DonationFormData>({
    resolver: zodResolver(donationSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      amount: 0.00,
      address: '',
      country: '',
      city: '',
      state: '',
      zip: '',
      // Dynamic fields will be initialized with empty strings (excluding disabled fields)
      ...donationSettings.dynamicFields
        .filter(field => !field.disabled)
        .reduce((acc, field) => {
          acc[field.name] = '';
          return acc;
        }, {} as Record<string, string>)
    }
  });
  
  const { register, handleSubmit: _handleSubmit, watch, setValue, formState: { errors } } = form;
  const watchAmount = watch('amount');
  
  // Calculate platform fee and total amount
  useEffect(() => {
    // Parse watchAmount to ensure it's a number
    const amount = parseFloat(watchAmount as any) || 0;
    
    if (showFeeToCustomer) {
      const feePercentage = 3;
      const fee = amount * (feePercentage / 100);
      setPlatformFeeAmount(fee);
      setTotalAmount(amount + fee);
    } else {
      setPlatformFeeAmount(0);
      setTotalAmount(amount);
    }
  }, [watchAmount, showFeeToCustomer, customerCoversFee]);
  
  // Update amount when preset amount changes
  useEffect(() => {
    if (selectedPresetAmount !== null && !customAmount) {
      setValue('amount', selectedPresetAmount);
    }
  }, [selectedPresetAmount, customAmount, setValue]);
  
  // Pre-select amount from URL parameter, or auto-select if only 1 preset
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlAmount = parseFloat(params.get('amount') || '');
    const presets = donationSettings.showPresetAmounts ? (donationSettings.presetAmounts || []) : [];

    if (urlAmount > 0) {
      // URL amount takes priority
      if (presets.includes(urlAmount)) {
        setSelectedPresetAmount(urlAmount);
        setCustomAmount(false);
      } else {
        setCustomAmount(true);
        setSelectedPresetAmount(null);
        setUrlAmountOverride(true);
      }
      setValue('amount', urlAmount, { shouldValidate: true, shouldDirty: true });
      return;
    }

    // Auto-select when only 1 preset amount
    if (presets.length === 1) {
      setSelectedPresetAmount(presets[0]);
      setValue('amount', presets[0]);
      setCustomAmount(false);
      return;
    }

    // Legacy: default preset when custom amounts are disabled
    if (donationSettings.allowCustomAmount === false && donationSettings.defaultPresetAmount) {
      setSelectedPresetAmount(donationSettings.defaultPresetAmount);
      setValue('amount', donationSettings.defaultPresetAmount);
      setCustomAmount(false);
    }
  }, []);
  
  // Handle preset amount selection
  const handlePresetAmountClick = (amount: number) => {
    setSelectedPresetAmount(amount);
    setCustomAmount(false);
  };
  
  // Handle custom amount toggle
  const handleCustomAmountToggle = () => {
    setCustomAmount(true);
    setSelectedPresetAmount(null);
    // Set focus and select text using ref
    setTimeout(() => {
      if (amountInputRef.current) {
        amountInputRef.current.focus();
        amountInputRef.current.select();
      }
    }, 10); // Keep small delay
  };
  
  // Handle payment success for credit card
  const handlePaymentSuccess = (transactionId: string, cardLast4: string, paymentMethod: string) => {
    console.log('Payment successful:', { transactionId, cardLast4, paymentMethod });
    
    // The donation record is already created server-side in the donation-process endpoint
    // Just show success and handle redirect
    setIsSuccess(true);
    
    // Redirect to thank you page if configured (after confirmation dialog acknowledgement)
    if (donationSettings.thankYouRedirectUrl) {
      window.location.href = donationSettings.thankYouRedirectUrl;
      return;
    }
  };
  
  // Handle payment success for ACH payments
  const handleACHPaymentSuccess = (transactionId: string) => {
    console.log('ACH Payment successful:', { transactionId });
    
    // The donation record is already created server-side in the ACH process endpoint
    // Just show success and handle redirect
    setIsSuccess(true);
    
    // Redirect to thank you page if configured
    if (donationSettings.thankYouRedirectUrl) {
      window.location.href = donationSettings.thankYouRedirectUrl;
      return;
    }
  };
  
  // Handle payment option selection (Credit Card or ACH)
  const handlePaymentOptionSelect = (paymentType: 'creditcard' | 'ach') => {
    // Validate amount
    const amount = parseFloat(watchAmount as any) || 0;
    
    // Check for minimum amount
    if (amount <= 0) {
      setError('Please enter an amount greater than 0');
      return;
    }
    
    // Validate form
    form.trigger().then(isValid => {
      if (!isValid) {
        setError('Please complete all required fields');
        return;
      }
      
      setError(null);
      const data = form.getValues();
      
      // Create donation data
      const donationData = {
        companyId,
        amount: amount,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email || '',
        address: data.address || '',
        country: data.country || '',
        city: data.city || '',
        state: data.state || '',
        zip: data.zip || '',
        status: 'pending' as const,
        platformFeeEnabled: paymentType === 'creditcard' && showFeeToCustomer,
        platformFeeAmount: paymentType === 'creditcard' && showFeeToCustomer ? platformFeeAmount : 0,
        platformFeePercentage: paymentType === 'creditcard' && showFeeToCustomer ? 3 : 0,
        description: donationSettings.description || '',
        dynamicFields: donationSettings.dynamicFields.reduce((acc, field) => {
          acc[field.name] = data[field.name as keyof typeof data] as string || '';
          return acc;
        }, {} as Record<string, string>),
        createdAt: new Date()
      };
      
      // Store donation data for use after payment
      setDonationDetails(donationData);
      
      // Open the appropriate payment modal
      if (paymentType === 'creditcard') {
        setIsPaymentModalOpen(true);
      } else {
        setIsACHPaymentModalOpen(true);
      }
    });
  };
  
  // Original form submission handler - keep for reference but it's not used anymore
  const _onSubmit = useCallback(async (data: DonationFormData) => {
    setError(null);
    setIsProcessing(true);
    
    try {
      // Ensure amount is a valid number before proceeding
      const amount = parseFloat(watchAmount as any) || 0;
      
      // Validate minimum amount
      if (amount <= 0) {
        throw new Error('Please enter an amount greater than 0');
      }
      
      // Create donation data
      const donationData = {
        companyId,
        amount: amount,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email || '',
        address: data.address || '',
        country: data.country || '',
        city: data.city || '',
        state: data.state || '',
        zip: data.zip || '',
        status: 'pending' as const,
        platformFeeEnabled: showFeeToCustomer,
        platformFeeAmount: showFeeToCustomer ? platformFeeAmount : 0,
        platformFeePercentage: showFeeToCustomer ? 3 : 0,
        description: donationSettings.description || '',
        dynamicFields: donationSettings.dynamicFields.reduce((acc, field) => {
          acc[field.name] = data[field.name as keyof typeof data] as string || '';
          return acc;
        }, {} as Record<string, string>),
        createdAt: new Date()
      };
      
      // Store donation data for use after payment
      setDonationDetails(donationData);
      
      // Create a virtual invoice for the payment modal
      const _invoiceData = {
        id: `donation-${Date.now()}-${uuidv4().substring(0, 8)}`,
        invoiceNumber: `DON-${Date.now()}`,
        customerName: `${data.firstName} ${data.lastName}`,
        customerEmail: data.email || '',
        companyId: companyId,
        customerId: `donor-${Date.now()}`,
        street: data.address,
        city: data.city,
        state: data.state,
        zip: data.zip
      };
      
      // Open payment modal
      setIsPaymentModalOpen(true);
      setIsProcessing(false);
      
    } catch (error) {
      console.error('Submit error:', error);
      setError(error instanceof Error ? error.message : 'An error occurred during form submission');
      setIsProcessing(false);
    }
  }, [companyId, watchAmount, donationSettings, platformFeeAmount]);
  
  // Render success message
  if (isSuccess) {
    return (
      <Card className="max-w-2xl mx-auto shadow-lg">
        <CardContent className="pt-6 px-8 pb-6 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full mx-auto flex items-center justify-center shadow-inner">
            <CheckCircle2 className="h-10 w-10 text-green-500" />
          </div>
          <CardTitle className="mt-4 text-2xl font-bold text-gray-900">Thank You!</CardTitle>
          <CardDescription className="mt-2 text-gray-600 max-w-md mx-auto">
            {donationSettings.thankYouMessage || 'Your generous support is greatly appreciated. A thank you email has been sent to your address.'}
          </CardDescription>
          
          {donationSettings.thankYouRedirectUrl ? (
            <div className="mt-4">
              <p className="text-sm text-gray-500">Redirecting you to our thank you page...</p>
              <div className="mt-2 w-16 h-1 bg-blue-100 mx-auto rounded-full animate-pulse"></div>
            </div>
          ) : (
            <Button 
              onClick={() => window.location.reload()}
              className="mt-6"
              variant="default"
            >
              Make Another Donation
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }
  
  return (
    <>
      <Card className="max-w-2xl mx-auto shadow-lg">
        <CardHeader 
          className="px-6 py-8 relative overflow-hidden rounded-t-xl"
          style={{ 
            background: donationSettings.headerBackgroundColor || 'linear-gradient(to right, #2563eb, #4f46e5, #7e22ce)',
            color: donationSettings.headerTextColor || '#ffffff' 
          }}
        >
          <div className="absolute top-0 left-0 w-full h-full opacity-10">
            <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
              <path d="M0,0 L100,0 L100,100 L0,100 Z" fill="url(#header-pattern)" />
            </svg>
            <defs>
              <pattern id="header-pattern" patternUnits="userSpaceOnUse" width="100" height="100">
                <path d="M0 10 C 20 0, 50 0, 100 10 L 100 0 L 0 0 Z" fill="rgba(255,255,255,0.1)" />
              </pattern>
            </defs>
          </div>
          <div className="relative z-10">
            <CardTitle 
              className="text-2xl font-bold"
              style={{ color: donationSettings.headerTextColor || '#ffffff' }}
            >
              {donationSettings.title}
            </CardTitle>
            {donationSettings.subtitle && (
              <CardDescription 
                className="mt-1 text-base opacity-90"
                style={{ color: donationSettings.headerTextColor ? `${donationSettings.headerTextColor}99` : 'rgba(219, 234, 254, 0.9)' }}
              >
                {donationSettings.subtitle}
              </CardDescription>
            )}
          </div>
        </CardHeader>
        
        <CardContent className="p-5">
          <form onSubmit={(e) => { e.preventDefault(); }} className="space-y-4">
            {/* Description */}
            {donationSettings.description && (
              <div className="text-gray-600 text-sm leading-relaxed border-l-4 border-blue-100 pl-3 py-1">
                {donationSettings.description}
              </div>
            )}
            
            {/* Error display */}
            {error && (
              <Alert variant="destructive" className="py-2">
                <AlertCircle className="h-4 w-4 mr-2" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            {/* Donation Amount */}
            <div className="space-y-3">
              <div className="flex items-center">
                <Badge variant="outline" className="mr-2 bg-blue-50 text-blue-700 hover:bg-blue-50 border-blue-200 h-5 w-5 p-0 flex items-center justify-center">1</Badge>
                <h3 className="text-base font-semibold text-gray-800">{donationSettings.amountLabel || "Donation Amount"}</h3>
              </div>
              
              {/* Preset amounts */}
              {donationSettings.showPresetAmounts && donationSettings.presetAmounts.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 my-2">
                  {donationSettings.presetAmounts.map((amount) => (
                    <Button
                      key={amount}
                      type="button"
                      variant={selectedPresetAmount === amount && !customAmount ? "default" : "outline"}
                      onClick={() => handlePresetAmountClick(amount)}
                      className="h-9"
                      size="sm"
                    >
                      ${amount}
                    </Button>
                  ))}
                  
                  {/* Custom amount button - only show if allowed */}
                  {donationSettings.allowCustomAmount !== false && (
                    <Button
                      type="button"
                      variant={customAmount ? "default" : "outline"}
                      onClick={handleCustomAmountToggle}
                      className="h-9"
                      size="sm"
                    >
                      Custom
                    </Button>
                  )}
                </div>
              )}
              
              {/* Custom amount input - show when no presets, custom is selected, or URL amount override */}
              {(!donationSettings.showPresetAmounts || (customAmount && (donationSettings.allowCustomAmount !== false || urlAmountOverride))) && (
                <div className="mt-2">
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 z-10">
                      <span className="text-gray-500 text-base">$</span>
                    </div>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      min="0.01"
                      placeholder="0.00"
                      className="pl-8 h-9"
                      {...register('amount')}
                      ref={(instance) => {
                        register('amount').ref(instance);
                        amountInputRef.current = instance;
                      }}
                      onFocus={() => {
                        setTimeout(() => {
                          amountInputRef.current?.select();
                        }, 0);
                      }}
                    />
                  </div>
                  {errors.amount && (
                    <p className="mt-1 text-xs text-destructive flex items-center">
                      <AlertCircle className="h-3 w-3 mr-1" />
                      {String(errors.amount.message)}
                    </p>
                  )}
                </div>
              )}
            </div>
            
            {/* Personal Information - Now section 2 */}
            <div className="pt-2">
              <div className="flex items-center mb-2">
                <Badge variant="outline" className="mr-2 bg-blue-50 text-blue-700 hover:bg-blue-50 border-blue-200 h-5 w-5 p-0 flex items-center justify-center">2</Badge>
                <h3 className="text-base font-semibold text-gray-800">Personal Information</h3>
              </div>
              
              <Card className="border border-gray-100">
                <CardContent className="p-3">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                    <div className="sm:col-span-1">
                      <Label htmlFor="firstName" className="mb-1 block text-xs">
                        First Name <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        type="text"
                        id="firstName"
                        className="h-8 text-sm"
                        {...register('firstName')}
                      />
                      {errors.firstName && (
                        <p className="mt-1 text-xs text-destructive flex items-center">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          {String(errors.firstName.message)}
                        </p>
                      )}
                    </div>
                    
                    <div className="sm:col-span-1">
                      <Label htmlFor="lastName" className="mb-1 block text-xs">
                        Last Name <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        type="text"
                        id="lastName"
                        className="h-8 text-sm"
                        {...register('lastName')}
                      />
                      {errors.lastName && (
                        <p className="mt-1 text-xs text-destructive flex items-center">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          {String(errors.lastName.message)}
                        </p>
                      )}
                    </div>
                    
                    <div className="sm:col-span-1">
                      <Label htmlFor="email" className="mb-1 block text-xs">
                        Email <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        type="email"
                        id="email"
                        className="h-8 text-sm"
                        {...register('email')}
                      />
                      {errors.email && (
                        <p className="mt-1 text-xs text-destructive flex items-center">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          {String(errors.email.message)}
                        </p>
                      )}
                    </div>
                    
                    {donationSettings.collectAddress !== false && (
                    <div className="sm:col-span-2">
                      <Label htmlFor="address" className="mb-1 block text-xs">
                        Address <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        type="text"
                        id="address"
                        className="h-8 text-sm"
                        {...register('address')}
                      />
                      {errors.address && (
                        <p className="mt-1 text-xs text-destructive flex items-center">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          {String(errors.address.message)}
                        </p>
                      )}
                    </div>
                    )}
                    
                    {donationSettings.collectAddress !== false && (
                    <div className="sm:col-span-1">
                      <Label htmlFor="country" className="mb-1 block text-xs">
                        Country <span className="text-destructive">*</span>
                      </Label>
                      <Select 
                        onValueChange={(value) => setValue('country', value)}
                        defaultValue=""
                      >
                        <SelectTrigger id="country" className="h-8 text-sm">
                          <SelectValue placeholder="Select a country" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[200px] overflow-y-auto">
                          {COUNTRIES.map((country) => (
                            <SelectItem key={country.value} value={country.value}>
                              {country.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.country && (
                        <p className="mt-1 text-xs text-destructive flex items-center">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          {String(errors.country.message)}
                        </p>
                      )}
                    </div>
                    )}
                    
                    {donationSettings.collectAddress !== false && (
                    <div className="sm:col-span-1">
                      <Label htmlFor="city" className="mb-1 block text-xs">
                        City
                      </Label>
                      <Input
                        type="text"
                        id="city"
                        className="h-8 text-sm"
                        {...register('city')}
                      />
                    </div>
                    )}
                    
                    {donationSettings.collectAddress !== false && (
                    <div className="sm:col-span-1">
                      <Label htmlFor="state" className="mb-1 block text-xs">
                        State/Province
                      </Label>
                      <Input
                        type="text"
                        id="state"
                        className="h-8 text-sm"
                        {...register('state')}
                      />
                    </div>
                    )}
                    
                    {donationSettings.collectAddress !== false && (
                    <div className="sm:col-span-1">
                      <Label htmlFor="zip" className="mb-1 block text-xs">
                        Zip / Postal Code <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        type="text"
                        id="zip"
                        className="h-8 text-sm"
                        {...register('zip')}
                      />
                      {errors.zip && (
                        <p className="mt-1 text-xs text-destructive flex items-center">
                          <AlertCircle className="h-3 w-3 mr-1" />
                          {String(errors.zip.message)}
                        </p>
                      )}
                    </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
            
            {/* Dynamic fields - Now section 3 */}
            {donationSettings.dynamicFields.length > 0 && (
              <div className="pt-2">
                <div className="flex items-center mb-2">
                  <Badge variant="outline" className="mr-2 bg-blue-50 text-blue-700 hover:bg-blue-50 border-blue-200 h-5 w-5 p-0 flex items-center justify-center">3</Badge>
                  <h3 className="text-base font-semibold text-gray-800">Additional Information</h3>
                </div>
                
                <Card className="border border-gray-100">
                  <CardContent className="p-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {donationSettings.dynamicFields
                      .filter(field => !field.disabled) // Filter out disabled fields
                      .map((field) => (
                      <div key={field.id}>
                        <Label htmlFor={field.name} className="mb-1 block text-xs">
                          {field.label} {field.required && <span className="text-destructive">*</span>}
                        </Label>
                        
                        {field.type === 'select' && field.options ? (
                          <Select 
                            onValueChange={(value) => setValue(field.name, value)}
                            defaultValue=""
                          >
                            <SelectTrigger id={field.name} className="h-8 text-xs">
                              <SelectValue placeholder="Select an option" />
                            </SelectTrigger>
                            <SelectContent>
                              {field.options?.filter(option => option.trim() !== '').map((option) => (
                                <SelectItem key={option} value={option}>
                                  {option}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input
                            type={field.type === 'number' ? 'number' : 'text'}
                            id={field.name}
                            className="h-8 text-sm"
                            {...register(field.name)}
                          />
                        )}
                        
                        {errors[field.name] && (
                          <p className="mt-1 text-xs text-destructive flex items-center">
                            <AlertCircle className="h-3 w-3 mr-1" />
                            {String(errors[field.name]?.message || '')}
                          </p>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </div>
            )}
            
            {/* Payment Options */}
            <div className="pt-4 space-y-4">
              <h3 className="text-base font-semibold text-gray-800 text-center mb-1">
                Select Payment Method
              </h3>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Credit card payment option */}
                <Card 
                  className="cursor-pointer hover:shadow-md transition-shadow duration-200 border-blue-100 overflow-hidden"
                >
                  <CardContent className="p-0">
                    <div className="p-4 flex flex-col items-center text-center">
                      <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mb-3">
                        <CreditCard className="h-6 w-6 text-blue-600" />
                      </div>
                      <h4 className="font-medium text-gray-900 text-lg">Credit Card</h4>
                      
                      {isAutoFee && (
                        <div className="mt-1 mb-2">
                          <div className="text-xs text-gray-500 font-medium flex items-center justify-center">
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-400 mr-1.5"></span>
                            3% platform fee
                          </div>
                        </div>
                      )}

                      {isCustomerChoiceFee && (
                        <label className="mt-2 mb-1 flex items-start gap-2 cursor-pointer text-left px-2">
                          <input
                            type="checkbox"
                            checked={customerCoversFee}
                            onChange={(e) => setCustomerCoversFee(e.target.checked)}
                            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary"
                          />
                          <span className="text-xs text-gray-600 leading-tight">
                            {donationSettings.platformFeeText || 'I\'d like to cover the processing fee so 100% of my payment goes through.'}
                          </span>
                        </label>
                      )}

                      <div className="text-base font-semibold text-gray-900 my-2">
                        ${totalAmount.toFixed(2)}
                      </div>

                      <Button
                        onClick={() => handlePaymentOptionSelect('creditcard')}
                        className="w-full mt-2 bg-green-600 hover:bg-green-700"
                        variant="default"
                      >
                        Pay with Card
                      </Button>
                    </div>
                  </CardContent>
                </Card>
                
                {/* ACH payment option - only show if enabled */}
                {donationSettings.acceptACH && (
                  <Card 
                    className="cursor-pointer hover:shadow-md transition-shadow duration-200 border-blue-100 overflow-hidden"
                  >
                    <CardContent className="p-0">
                      <div className="p-4 flex flex-col items-center text-center">
                        <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mb-3">
                          <Building2 className="h-6 w-6 text-blue-600" />
                        </div>
                        <h4 className="font-medium text-gray-900 text-lg">Bank Account</h4>
                        
                        <div className="mt-1 mb-2">
                          <div className="text-xs text-green-600 font-medium flex items-center justify-center">
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-600 mr-1.5"></span>
                            No platform fees
                          </div>
                        </div>
                        
                        <div className="text-base font-semibold text-gray-900 my-2">
                          ${parseFloat(watchAmount as any).toFixed(2) || '0.00'}
                        </div>
                        
                        <Button 
                          onClick={() => handlePaymentOptionSelect('ach')}
                          className="w-full mt-2"
                          variant="default"
                        >
                          Pay with Bank
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
              
              <div className="mt-3 flex items-center justify-center text-xs text-muted-foreground">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                Your payment information is securely processed via our payment gateway
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
      
      {/* Payment Modals */}
      {donationDetails && (
        <>
          {/* Credit Card Payment Modal */}
          <PaymentModal
            open={isPaymentModalOpen}
            onOpenChange={setIsPaymentModalOpen}
            invoice={{
              id: `donation-${Date.now()}-${uuidv4().substring(0, 8)}`,
              invoiceNumber: `DON-${Date.now()}`,
              paymentDescription: donationSettings.description || undefined,
              customerName: `${donationDetails.firstName} ${donationDetails.lastName}`,
              customerEmail: donationDetails.email || '',
              companyId: companyId,
              customerId: `donor-${Date.now()}`,
              street: donationDetails.address,
              city: donationDetails.city,
              state: donationDetails.state,
              zip: donationDetails.zip,
              dynamicFields: donationDetails.dynamicFields || {}
            }}
            invoiceAmount={donationDetails.amount}
            platformFee={showFeeToCustomer ? 3 : 0}
            enablePlatformFee={showFeeToCustomer}
            // Provide donation context so Stripe flow records the donation
            donationBaseAmount={donationDetails.amount}
            donationDynamicFields={donationDetails.dynamicFields || {}}
            onPaymentSuccess={handlePaymentSuccess}
          />
          
          {/* ACH Payment Modal */}
          <ACHPaymentModal
            open={isACHPaymentModalOpen}
            onOpenChange={setIsACHPaymentModalOpen}
            invoice={{
              id: `donation-${Date.now()}-${uuidv4().substring(0, 8)}`,
              invoiceNumber: `DON-${Date.now()}`,
              paymentDescription: donationSettings.description || undefined,
              customerName: `${donationDetails.firstName} ${donationDetails.lastName}`,
              customerEmail: donationDetails.email || '',
              companyId: companyId,
              customerId: `donor-${Date.now()}`,
              street: donationDetails.address,
              city: donationDetails.city,
              state: donationDetails.state,
              zip: donationDetails.zip,
              dynamicFields: donationDetails.dynamicFields || {}
            }}
            invoiceAmount={donationDetails.amount}
            onPaymentSuccess={handleACHPaymentSuccess}
          />
        </>
      )}
    </>
  );
} 
